import React from "react";
import { XP_DISCLAIMER } from "../../config/supernovaXp";
import SupernovaOrb from "./SupernovaOrb";
import { METRIC, STAKE_UNAVAILABLE_NOTE } from "./supernovaSummary";
import { buildStakeRows } from "./supernovaExitQuote";
import { fmtCount, fmtDate, fmtMultiplier, fmtXpExact } from "./supernovaFormat";
import { ChainErrorCard, Skeleton } from "./SupernovaStates";

/**
 * "Active stakes" — one row per open lot the staking contract holds for this
 * member, with the exit that lot is currently eligible for.
 *
 * WHAT EACH COLUMN MEANS, because two of them are easy to confuse:
 *   Earned so far  what daily accrual has credited at the lot's CONTRACTED
 *                  multiplier — i.e. what the member has if they hold to term.
 *   At maturity    the model's ceiling for the full term. Not a forecast, not a
 *                  promise, and explicitly not what an early exit would pay:
 *                  that is quoted, line by line, in the unstake modal.
 *
 * NOTHING HERE IS COMPUTED. Every figure comes from supernovaExitQuote, which
 * only arranges what config/supernovaXp returns.
 *
 * THE STATES ARE THE POINT. "We can't read your stakes" and "you have no
 * stakes" are different facts and render differently; neither renders as a zero
 * or an empty table. The lots themselves are now a real chain read
 * (supernovaStakeRead), so the unavailable state below is the fallback, not the
 * everyday one — and it stays, because a build without a read descriptor must
 * still say so rather than draw an empty table.
 *
 * @param {Object} props
 * @param {Object|null} props.staked   getStaked() result
 * @param {string} props.status        METRIC status for the stake read
 * @param {string|null} [props.note]   copy for the unavailable state
 * @param {string|null} [props.message] member-safe copy for the error state
 * @param {(lotId: string) => void} [props.onUnstake]
 * @param {() => void} [props.onRetry]
 * @param {boolean} [props.retrying]
 * @param {number} [props.nowMs]       injectable clock (tests)
 */
const SupernovaStakeTable = ({
  staked,
  status,
  note,
  message,
  onUnstake,
  onRetry,
  retrying = false,
  nowMs,
}) => {
  const now = nowMs ?? Date.now();

  if (status === METRIC.LOADING || status === METRIC.IDLE) return <StakeTableSkeleton />;

  if (status === METRIC.UNAVAILABLE) {
    return (
      <Shell>
        {/* TODO(backend, supervised): the lots themselves come from
            supernovaChain.getStaked, which reports itself unavailable until the
            staking contract's read descriptor (SUPERNOVA_STAKING_READ) is filled
            in from the deployed contract, or the engine serves GET /stake/status.
            An empty table here would read as "you have no stakes" — which we do
            not know — so this says what we actually know instead. */}
        <p className="text-body text-arc-ink-2" data-testid="supernova-stakes-unavailable">
          {note || STAKE_UNAVAILABLE_NOTE}
        </p>
        <p className="text-caption text-arc-ink-3 mt-2">
          This is a reading problem on our side, not a change to your position:
          anything you have staked is still staked, still accruing, and still
          yours to unstake from your own wallet.
        </p>
      </Shell>
    );
  }

  if (status === METRIC.ERROR) {
    return (
      <ChainErrorCard
        title="Couldn't read your active stakes"
        message={message || "Couldn't read your stakes from Arbitrum. Try again."}
        onRetry={onRetry}
        retrying={retrying}
      />
    );
  }

  const rows = buildStakeRows(staked, now);

  if (rows.length === 0) {
    return (
      <Shell>
        <p className="text-body text-arc-ink-3" data-testid="supernova-stakes-empty">
          No active stakes. Staking a Supernova locks it in the contract for the
          duration you pick and starts accruing XP daily.
        </p>
      </Shell>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="sn-table" data-testid="supernova-stakes-table">
          <caption className="sr-only">
            Your open Supernova stake lots, what each has earned so far, and when
            each matures.
          </caption>
          <thead>
            <tr>
              <Th>Stake</Th>
              <Th>Lock</Th>
              <Th>Matures</Th>
              <Th numeric>Earned so far</Th>
              <Th numeric>At maturity</Th>
              <Th>Status</Th>
              <Th>
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {/* A lot we couldn't read may have no id, so the index backs the
                key up rather than letting two nulls collide. */}
            {rows.map((row, i) => (
              <StakeRow key={row.lotId || `row-${i}`} row={row} onUnstake={onUnstake} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-caption text-arc-ink-4 mt-3.5">
        Earned so far accrues daily at each lock&apos;s own rate. At maturity is
        the model&apos;s maximum for the full term under today&apos;s parameters,
        not a forecast — and not what an early exit pays. {XP_DISCLAIMER}
      </p>
    </>
  );
};

/* ── Row ────────────────────────────────────────────────────────────────── */

const UNREADABLE =
  "We couldn't read the details of this stake, so we can't quote an exit for it. Nothing about it has changed on-chain.";

const StakeRow = ({ row, onUnstake }) => {
  const canUnstake = row.readable && typeof onUnstake === "function";
  return (
    <tr data-testid="supernova-stake-row">
      <Td first>
        <span className="flex items-center gap-2.5">
          {/* Decorative: the cell names the token right beside it. */}
          <SupernovaOrb tokenId={row.tokenId} name={null} size={24} />
          <span className="tnum">
            {row.readable ? `${fmtCount(row.qty)}× ${row.tokenName}` : row.tokenName || "Unknown stake"}
          </span>
        </span>
      </Td>
      <Td numeric>
        {row.readable ? (
          <>
            {row.lockChipLabel}{" "}
            <span className="text-arc-ink-4">{fmtMultiplier(row.multiplier)}</span>
          </>
        ) : (
          "—"
        )}
      </Td>
      <Td numeric>{fmtDate(row.unlockAtMs)}</Td>
      <Td numeric>{row.readable ? fmtXpExact(row.earnedXp) : "—"}</Td>
      <Td numeric className="text-arc-ink-4">
        {row.readable ? fmtXpExact(row.xpAtMaturity) : "—"}
      </Td>
      <Td>
        {row.readable ? (
          <span className={`sn-st ${row.matured ? "sn-st-mat" : "sn-st-lock"}`}>
            {row.matured ? "Matured" : "Locked"}
          </span>
        ) : (
          <span className="sn-st sn-st-unread" title={UNREADABLE}>
            Unreadable
          </span>
        )}
      </Td>
      <Td className="text-right">
        <button
          type="button"
          className="sn-ghost"
          disabled={!canUnstake}
          title={row.readable ? undefined : UNREADABLE}
          onClick={canUnstake ? () => onUnstake(row.lotId) : undefined}
        >
          Unstake
          {!row.readable && <span className="sr-only"> — {UNREADABLE}</span>}
        </button>
      </Td>
    </tr>
  );
};

/* ── Chrome ─────────────────────────────────────────────────────────────── */

/** The panel the non-table states sit in — the table itself is bare, as in the
    mockup, so its hairlines are the only structure on the section. */
const Shell = ({ className = "", children }) => (
  <div className={`sn-panel ${className}`.trim()}>{children}</div>
);

const Th = ({ numeric = false, children }) => (
  <th scope="col" className={numeric ? "sn-num" : undefined}>
    {children}
  </th>
);

const Td = ({ numeric = false, first = false, className = "", children }) => (
  <td
    className={[
      "align-middle",
      first ? "text-arc-ink font-medium" : "",
      numeric ? "sn-num" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
  >
    {children}
  </td>
);

/**
 * Skeleton rows, per DESIGN_SYSTEM §7 — never a spinner, never a zero.
 *
 * Deliberately NOT a live region: both chain slices load in the same pass, and
 * the collection skeleton above already announces the read. A second polite
 * region firing at the same moment is noise to a screen reader, not information.
 */
const StakeTableSkeleton = () => (
  <Shell>
    <span className="sr-only">Reading your active stakes from Arbitrum…</span>
    <div className="space-y-3">
      {[0, 1].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton radius="rounded-full" className="w-6 h-6 flex-none" />
          <Skeleton className="h-[13px] flex-1 max-w-[220px]" />
          <Skeleton className="h-[13px] w-[80px] flex-none" />
          <Skeleton className="h-[26px] w-[76px] flex-none" />
        </div>
      ))}
    </div>
  </Shell>
);

export default SupernovaStakeTable;
